import React from 'react';

interface InsuranceSectionProps {
  insurance: {
    provider: string;
    planType: string;
    memberId: string;
    groupNumber: string;
    copay: string;
    deductible: string;
    outOfPocketMax: string;
  };
  onChange: (insurance: any) => void;
  isInitialSetup?: boolean;
}

const insuranceProviders = [
  { value: 'aetna', label: 'Aetna' },
  { value: 'anthem', label: 'Anthem' },
  { value: 'blue_cross_blue_shield', label: 'Blue Cross Blue Shield' },
  { value: 'cigna', label: 'Cigna' },
  { value: 'humana', label: 'Humana' },
  { value: 'kaiser_permanente', label: 'Kaiser Permanente' },
  { value: 'molina_healthcare', label: 'Molina Healthcare' },
  { value: 'unitedhealth', label: 'UnitedHealthcare' },
  { value: 'wellcare', label: 'WellCare' },
  { value: 'medicaid', label: 'Medicaid' },
  { value: 'medicare', label: 'Medicare' },
  { value: 'tricare', label: 'TRICARE' },
  { value: 'other', label: 'Other' }
];

const planTypes = [
  { value: 'HMO', label: 'HMO - Health Maintenance Organization' },
  { value: 'PPO', label: 'PPO - Preferred Provider Organization' },
  { value: 'POS', label: 'POS - Point of Service' },
  { value: 'EPO', label: 'EPO - Exclusive Provider Organization' },
  { value: 'HDHP', label: 'HDHP - High Deductible Health Plan' },
  { value: 'Medicare', label: 'Medicare' },
  { value: 'Medicaid', label: 'Medicaid' },
  { value: 'Other', label: 'Other' }
];

const InsuranceSection: React.FC<InsuranceSectionProps> = ({
  insurance,
  onChange,
  isInitialSetup = false
}) => {
  const handleChange = (field: string, value: string) => {
    onChange({
      ...insurance,
      [field]: value
    });
  };

  return (
    <div style={{
      background: '#1c2536',
      borderRadius: '0.75rem',
      padding: '1.5rem',
      color: 'white'
    }}>
      <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
        {/* Insurance Provider Dropdown */}
        <div>
          <label style={{ display: 'block', marginBottom: '0.5rem', color: '#d1d5db' }}>
            Insurance Provider
          </label>
          <select
            value={insurance.provider}
            onChange={(e) => handleChange('provider', e.target.value)}
            required={isInitialSetup}
            style={{
              width: '100%',
              padding: '0.5rem',
              borderRadius: '0.375rem',
              border: '1px solid rgba(156, 163, 175, 0.2)',
              background: 'rgba(255, 255, 255, 0.1)',
              color: 'white',
              fontSize: '1.1rem'
            }}
          >
            <option value="">Select Provider</option>
            {insuranceProviders.map((p) => (
              <option key={p.value} value={p.value} style={{ background: '#1c2536' }}>
                {p.label}
              </option>
            ))}
          </select>
        </div>

        {/* Plan Type Dropdown */}
        <div>
          <label style={{ display: 'block', marginBottom: '0.5rem', color: '#d1d5db' }}>
            Plan Type
          </label>
          <select
            value={insurance.planType}
            onChange={(e) => handleChange('planType', e.target.value)}
            required={isInitialSetup}
            style={{
              width: '100%',
              padding: '0.5rem',
              borderRadius: '0.375rem',
              border: '1px solid rgba(156, 163, 175, 0.2)',
              background: 'rgba(255, 255, 255, 0.1)',
              color: 'white',
              fontSize: '1.1rem'
            }}
          >
            <option value="">Select Plan Type</option>
            {planTypes.map((plan) => (
              <option key={plan.value} value={plan.value} style={{ background: '#1c2536' }}>
                {plan.label}
              </option>
            ))}
          </select>
        </div>

        {/* Member ID */}
        <div>
          <label style={{ display: 'block', marginBottom: '0.5rem', color: '#d1d5db' }}>
            Member ID
          </label>
          <input
            type="text"
            value={insurance.memberId}
            onChange={(e) => handleChange('memberId', e.target.value)}
            required={isInitialSetup}
            style={{
              width: '100%',
              padding: '0.5rem',
              borderRadius: '0.375rem',
              border: '1px solid rgba(156, 163, 175, 0.2)',
              background: 'rgba(255, 255, 255, 0.1)',
              color: 'white',
              fontSize: '1.1rem'
            }}
          />
        </div>

        {/* Group Number */}
        <div>
          <label style={{ display: 'block', marginBottom: '0.5rem', color: '#d1d5db' }}>
            Group Number
          </label>
          <input
            type="text"
            value={insurance.groupNumber}
            onChange={(e) => handleChange('groupNumber', e.target.value)}
            required={isInitialSetup}
            style={{
              width: '100%',
              padding: '0.5rem',
              borderRadius: '0.375rem',
              border: '1px solid rgba(156, 163, 175, 0.2)',
              background: 'rgba(255, 255, 255, 0.1)',
              color: 'white',
              fontSize: '1.1rem'
            }}
          />
        </div>
      </div>

      <div style={{ 
        display: 'grid', 
        gap: '1rem', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        marginTop: '1.5rem',
        paddingTop: '1.5rem',
        borderTop: '1px solid rgba(156, 163, 175, 0.2)'
      }}>
        {/* Copay */}
        <div>
          <label style={{ display: 'block', marginBottom: '0.5rem', color: '#d1d5db' }}>
            Copay Amount
          </label>
          <div style={{ position: 'relative' }}>
            <span style={{ 
              position: 'absolute', 
              left: '0.75rem', 
              top: '50%', 
              transform: 'translateY(-50%)',
              color: '#d1d5db'
            }}>
              $
            </span>
            <input
              type="text"
              value={insurance.copay}
              onChange={(e) => handleChange('copay', e.target.value)}
              required={isInitialSetup}
              style={{
                width: '100%',
                padding: '0.5rem',
                paddingLeft: '1.5rem',
                borderRadius: '0.375rem',
                border: '1px solid rgba(156, 163, 175, 0.2)',
                background: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                fontSize: '1.1rem'
              }}
            />
          </div>
        </div>

        {/* Deductible */}
        <div>
          <label style={{ display: 'block', marginBottom: '0.5rem', color: '#d1d5db' }}>
            Deductible
          </label>
          <div style={{ position: 'relative' }}>
            <span style={{ 
              position: 'absolute', 
              left: '0.75rem', 
              top: '50%', 
              transform: 'translateY(-50%)',
              color: '#d1d5db'
            }}>
              $
            </span>
            <input
              type="text"
              value={insurance.deductible}
              onChange={(e) => handleChange('deductible', e.target.value)}
              required={isInitialSetup}
              style={{
                width: '100%',
                padding: '0.5rem',
                paddingLeft: '1.5rem',
                borderRadius: '0.375rem',
                border: '1px solid rgba(156, 163, 175, 0.2)',
                background: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                fontSize: '1.1rem'
              }}
            />
          </div>
        </div>

        {/* Out of Pocket Max */}
        <div>
          <label style={{ display: 'block', marginBottom: '0.5rem', color: '#d1d5db' }}>
            Out of Pocket Maximum
          </label>
          <div style={{ position: 'relative' }}>
            <span style={{ 
              position: 'absolute', 
              left: '0.75rem', 
              top: '50%', 
              transform: 'translateY(-50%)',
              color: '#d1d5db'
            }}>
              $
            </span>
            <input
              type="text"
              value={insurance.outOfPocketMax}
              onChange={(e) => handleChange('outOfPocketMax', e.target.value)}
              required={isInitialSetup}
              style={{
                width: '100%',
                padding: '0.5rem',
                paddingLeft: '1.5rem',
                borderRadius: '0.375rem',
                border: '1px solid rgba(156, 163, 175, 0.2)',
                background: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                fontSize: '1.1rem'
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default InsuranceSection;
